import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Plus, Trash2, ArrowLeft, Search, Upload, Users, ListPlus,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toolkitTitle } from "@/lib/toolkits";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function parseEmails(text) {
  return Array.from(
    new Set(
      (text || "")
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => EMAIL_RE.test(e))
    )
  );
}

export default function AdminApprovedEmails() {
  usePageMeta({ title: "Admin · Approved Emails | Southern Black Girls Toolkits", description: "Staff dashboard." });
  const { user, loading: authLoading } = useAuth();
  const { toolkit } = useParams();

  if (authLoading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  if (!user || !user.is_staff)
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteNav />
        <div className="mx-auto max-w-md px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-black text-sbg-burgundy">Admin access required</h1>
          <p className="mt-3 text-sbg-burgundy/70">Sign in with an authorized admin email to manage approved emails.</p>
          <div className="mt-6"><Link to="/admin/login" className="rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Sign in</Link></div>
        </div>
        <SiteFooter />
      </div>
    );

  return <Manager toolkit={toolkit} />;
}

function Manager({ toolkit }) {
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/approved-emails?toolkit=${toolkit}`);
      setApproved(data);
      setSelected(new Set());
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 401) toast.error("Could not load approved emails.");
    } finally {
      setLoading(false);
    }
  }, [toolkit]);

  useEffect(() => { load(); }, [load]);

  const addEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address."); return; }
    try {
      await api.post("/admin/approved-emails", { email, toolkit });
      setNewEmail("");
      toast.success(`${email} added to the approved list.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that email.");
    }
  };

  const submitBulk = async (emails) => {
    if (emails.length === 0) { toast.error("No valid email addresses found."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/approved-emails/bulk", { emails, toolkit });
      toast.success(`${data.added} email${data.added === 1 ? "" : "s"} added to the approved list.`);
      setBulkText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add those emails.");
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkPaste = () => submitBulk(parseEmails(bulkText));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const emails = parseEmails(String(reader.result));
      if (emails.length === 0) { toast.error("No valid emails found in that file."); return; }
      submitBulk(emails);
    };
    reader.onerror = () => toast.error("Could not read that file.");
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeEmail = async (email) => {
    try {
      await api.delete(`/admin/approved-emails/${encodeURIComponent(email)}?toolkit=${toolkit}`);
      toast.success(`${email} removed.`);
      load();
    } catch {
      toast.error("Could not remove that email.");
    }
  };

  const removeSelected = async () => {
    const emails = Array.from(selected);
    if (emails.length === 0) return;
    try {
      const { data } = await api.post("/admin/approved-emails/bulk-delete", { emails, toolkit });
      toast.success(`${data.removed} email${data.removed === 1 ? "" : "s"} removed.`);
      load();
    } catch {
      toast.error("Could not remove the selected emails.");
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = approved.filter((a) => !q || a.email.toLowerCase().includes(q));
  const allVisibleSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.email));

  const toggle = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((a) => next.delete(a.email));
      else filtered.forEach((a) => next.add(a.email));
      return next;
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-5xl px-5 py-10">
        <Link to={`/admin/${toolkit}`} data-testid="approved-back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to {toolkitTitle(toolkit)}
        </Link>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sbg-fuchsia text-white">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-black text-sbg-burgundy" data-testid="approved-page-title">Approved winner emails</h1>
            <p className="text-sm text-sbg-burgundy/60">
              Anyone on this list gets a live {toolkitTitle(toolkit)} page instantly. Everyone else is held for review.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {/* Add one */}
          <div className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-sbg-fuchsia" />
              <h2 className="font-display text-lg font-bold text-sbg-burgundy">Add a single email</h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <input
                data-testid="approved-email-input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
                placeholder="winner@example.com"
                className="min-w-[200px] flex-1 rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-sbg-fuchsia"
              />
              <button data-testid="approved-email-add-btn" onClick={addEmail} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-fuchsia px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>

          {/* Bulk add */}
          <div className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10">
            <div className="flex items-center gap-2">
              <ListPlus className="h-5 w-5 text-pt-purple" />
              <h2 className="font-display text-lg font-bold text-sbg-burgundy">Add many at once</h2>
            </div>
            <p className="mt-1 text-xs text-sbg-burgundy/60">Paste emails separated by commas, spaces, or new lines — or upload a CSV.</p>
            <textarea
              data-testid="approved-bulk-textarea"
              rows={3}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"winner1@example.com\nwinner2@example.com, winner3@example.com"}
              className="mt-3 w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-sbg-fuchsia"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="approved-bulk-add-btn" onClick={onBulkPaste} disabled={bulkBusy} className="inline-flex items-center gap-1.5 rounded-full bg-pt-purple px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListPlus className="h-4 w-4" />} Add list
              </button>
              <input ref={fileRef} data-testid="approved-csv-input" type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" />
              <button data-testid="approved-csv-btn" onClick={() => fileRef.current?.click()} disabled={bulkBusy} className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20 disabled:opacity-60">
                <Upload className="h-4 w-4" /> Upload CSV
              </button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="mt-6 rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10" data-testid="approved-list-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-sbg-fuchsia" />
              <h2 className="font-display text-lg font-bold text-sbg-burgundy">
                Approved list <span className="text-sbg-burgundy/50">({approved.length})</span>
              </h2>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-sbg-blush/40 px-3 py-2">
              <Search className="h-4 w-4 text-sbg-burgundy/50" />
              <input data-testid="approved-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search emails" className="w-44 bg-transparent text-sm outline-none placeholder:text-sbg-burgundy/40" />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sbg-raspberry" /></div>
          ) : filtered.length === 0 ? (
            <p className="mt-6 text-sm text-sbg-burgundy/50">{approved.length === 0 ? "No approved emails yet. Add some above." : "No emails match your search."}</p>
          ) : (
            <>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-sbg-raspberry/10 pb-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-sbg-burgundy">
                  <input data-testid="approved-select-all" type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} className="h-4 w-4 accent-sbg-fuchsia" />
                  Select all shown
                </label>
                {selected.size > 0 && (
                  <button data-testid="approved-bulk-remove-btn" onClick={() => setConfirm({ title: `Remove ${selected.size} email${selected.size === 1 ? "" : "s"}?`, description: "These addresses will no longer be auto-approved for instant publishing.", confirmLabel: "Remove selected", onConfirm: removeSelected })} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-burgundy px-4 py-2 text-xs font-bold text-white">
                    <Trash2 className="h-3.5 w-3.5" /> Remove selected ({selected.size})
                  </button>
                )}
              </div>
              <div className="mt-3 divide-y divide-sbg-raspberry/10">
                {filtered.map((a) => (
                  <div key={a.email} data-testid={`approved-row-${a.email}`} className="flex items-center justify-between gap-3 py-2.5">
                    <label className="flex min-w-0 items-center gap-3 text-sm font-semibold text-sbg-burgundy">
                      <input data-testid={`approved-check-${a.email}`} type="checkbox" checked={selected.has(a.email)} onChange={() => toggle(a.email)} className="h-4 w-4 flex-shrink-0 accent-sbg-fuchsia" />
                      <span className="truncate">{a.email}</span>
                    </label>
                    <button onClick={() => setConfirm({ title: "Remove email?", description: `${a.email} will no longer be auto-approved for instant publishing.`, confirmLabel: "Remove", onConfirm: () => removeEmail(a.email) })} data-testid={`approved-remove-${a.email}`} className="flex-shrink-0 rounded-full p-1.5 text-sbg-raspberry hover:bg-sbg-blush/60" title="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => { confirm?.onConfirm?.(); setConfirm(null); }}
      />
      <SiteFooter />
    </div>
  );
}
